import React, { useState } from "react";
import Sidebar from "../common/Sidebar";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { AdminAddProduct } from "../../Api/Admin/productServices"
import toast, { Toaster } from 'react-hot-toast';
const Sizes = ["XS", "S", "M", "L", "XL", "XXL"];
const AddProduct = () => {
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        originalPrice: "",
        discount: "",
        size: [],
        color: "",
        material: "",
        brand: "",
        category: "",
        description: "",
    });

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        const newImagePreviews = files.map((file) => URL.createObjectURL(file));
        setSelectedImages((prevImages) => [...prevImages, ...files]);
        setImagePreviews((prevPreviews) => [...prevPreviews, ...newImagePreviews]);
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSizeClick = (sizeValue) => {
        const isSelected = formData.size.some(s => s.size === sizeValue);
        let updatedSizes;
        
        if (isSelected) {
            updatedSizes = formData.size.filter(s => s.size !== sizeValue);
        } else {
            updatedSizes = [...formData.size, { size: sizeValue, inStock: true }];
        }
        
        setFormData({ ...formData, size: updatedSizes });
    };

    const validateForm = () => {
        const requiredFields = {
            title: "Product name",
            originalPrice: "Base price",
            color: "Color",
            material: "Material",
            brand: "Brand",
            category: "Category",
            description: "Description",
        };

        for (const [field, label] of Object.entries(requiredFields)) {
            if (!formData[field]) {
                toast.error(`${label} is required`);
                return false;
            }
        }

        if (formData.size.length === 0) {
            toast.error("Please select at least one size");
            return false;
        }

        if (!["male", "female", "unisex"].includes(formData.category)) {
            toast.error("Invalid category selected");
            return false;
        }

        if (selectedImages.length === 0) {
            toast.error("Please upload at least one image");
            return false;
        }

        // Validate numeric fields
        if (isNaN(formData.originalPrice) || formData.originalPrice <= 0) {
            toast.error("Please enter a valid price");
            return false;
        }

        if (isNaN(formData.discount) || formData.discount < 0 || formData.discount > 100) {
            toast.error("Please enter a valid discount percentage (0-100)");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Authentication error. Please login again.");
            return;
        }

        const formDataToSend = new FormData();
        
        // Append all form fields
        for (const key in formData) {
            if (key === 'size') {
                // Ensure size array is properly formatted with size and inStock properties
                const sizeArray = formData.size.map(sizeObj => ({
                    size: sizeObj.size,
                    inStock: true
                }));
                formDataToSend.append('size', JSON.stringify(sizeArray));
            } else {
                formDataToSend.append(key, formData[key]);
            }
        }

        // Append images
        selectedImages.forEach((image) => {
            formDataToSend.append("images", image);
        });

        setLoading(true);
        try {
            const res = await AdminAddProduct(formDataToSend, token);
            if (res.status === 201) {
                toast.success("Product added successfully");
                // Reset form
                setFormData({
                    title: "",
                    originalPrice: "",
                    discount: "",
                    size: [],
                    color: "",
                    material: "",
                    brand: "",
                    category: "",
                    description: "",
                });
                setSelectedImages([]);
                setImagePreviews([]);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                toast.error("Session expired. Please login again.");
            } else if (error.response?.status === 413) {
                toast.error("Images too large. Please upload smaller images.");
            } else {
                toast.error(error?.response?.data?.message || "Error uploading product. Please try again.");
            }
            console.error("Error uploading product:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen">
            <Sidebar />
            <Toaster position="top-right" />
            <div className="flex-1 p-6 bg-gray-50" style={{ marginLeft: '16rem' }}>
                <h1 className="text-2xl font-bold mb-6">Add New Product</h1>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Section - Form */}
                    <div className="space-y-6">
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Name Product</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter product name"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Description Product</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter product description"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                                rows="4"
                            ></textarea>
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Size</label>
                            <div className="flex space-x-4">
                                {Sizes.map((size) => (
                                    <button
                                        key={size}
                                        onClick={() => handleSizeClick(size)}
                                        className={`px-4 py-2 border rounded-lg hover:bg-gray-100 ${
                                            formData.size.some(s => s.size === size) ? "bg-blue-100" : ""
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Base Pricing</label>
                            <input
                                type="number"
                                name="originalPrice"
                                value={formData.originalPrice}
                                onChange={handleInputChange}
                                placeholder="$0.00"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            />
                        </div>
                      
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Discount</label>
                            <input
                                type="number"
                                name="discount"
                                value={formData.discount}
                                onChange={handleInputChange}
                                placeholder="Enter discount percentage 10-20 etc"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Category</label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            >
                                <option value="" disabled>
                                    Select a category
                                </option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="unisex">Unisex</option>
                                <option value="other">Other</option>
                            </select>
                        </div>


                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Color</label>
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleInputChange}
                                    placeholder="Color name"
                                    className="p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Material</label>
                            <input
                                type="text"
                                name="material"
                                value={formData.material}
                                onChange={handleInputChange}
                                placeholder="Enter material"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700 mb-2">Brand</label>
                            <input
                                type="text"
                                name="brand"
                                value={formData.brand}
                                onChange={handleInputChange}
                                placeholder="Enter brand"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
                            />
                        </div>
                    </div>

                    {/* Right Section - Image Upload */}
                    <div className="space-y-4">
                        <div className="flex space-x-2 overflow-x-auto">
                            {imagePreviews.map((image, index) => (
                                <img
                                    key={index}
                                    src={image}
                                    alt={`Selected ${index}`}
                                    className="w-70 h-40 object-cover rounded-lg border border-gray-200"
                                />
                            ))}
                        </div>
                        <label className="block font-medium text-gray-700 mb-2">Upload Images</label>
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 p-4 rounded-lg bg-gray-50">
                            <label
                                htmlFor="image-upload"
                                className="flex flex-col items-center justify-center cursor-pointer"
                            >
                                <AiOutlineCloudUpload className="text-2xl text-gray-500 mb-2" />
                                <span className="text-sm text-gray-500">Click to upload</span>
                            </label>
                            <input
                                id="image-upload"
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                        </div>

                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 mt-6">
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                        Save Draft
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                        {loading ? "Loading..." : "Add Product"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddProduct;
